import { ChangeDetectorRef, inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';

import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { ItemSelector, SelectItemStrategy } from './default-strategies/select-item-strategy';
import { Select2ListItemDirective } from './default-templates/list-item/list-item.directive';
import { Select2SelectedItemDirective } from './default-templates/selected-item/selected-item.directive';
import { Select2TriggerDirective } from './default-templates/trigger/trigger.directive';
import { HeaderTemplate, Select2Item, SelectContext, SelectFilterFn } from './types';

export const SELECT_FILTERED_ITEMS = new InjectionToken<BehaviorSubject<Select2Item[]>>(
  'Filtered items stream',
);

export const SELECT_ITEMS = new InjectionToken<BehaviorSubject<Select2Item[]>>('Items stream');

type SelectItemType<T extends SelectContext> = T extends { items: any[] }
  ? T['items'][number]
  : never;

@Injectable()
export class SelectController<
  Select extends { items: Select2Item[] } | null = { items: Select2Item[] },
> implements OnDestroy
{
  public filtersMap: Record<string, SelectFilterFn> = {};
  public headerTemplates: HeaderTemplate[] = [];
  public itemSelector =
    inject(ItemSelector, { optional: true }) ?? new SelectItemStrategy<SelectItemType<Select>>();

  public isOpen$ = new BehaviorSubject<boolean>(false);
  public clear$ = new Subject<void>();
  public itemsToRender$ = new BehaviorSubject<Select2Item[]>([]);
  public select$ = new Subject<Select2Item | Select2Item[]>();
  public keyDown$ = new Subject<KeyboardEvent>();
  public navigation$ = new BehaviorSubject<{ type: 'item'; index: number }>({
    type: 'item',
    index: 0,
  });
  public focusTrigger$ = new Subject<void>();
  public itemsRefresh$ = new Subject<void>();
  public detectChanges$ = new BehaviorSubject<void>(void 0);
  public virtualScroll$ = new BehaviorSubject<VirtualScrollerComponent | null>(null);

  public triggerDirective: Select2TriggerDirective<Select>;
  public listItemDirective: Select2ListItemDirective<Select>;
  public selectedItemDirective: Select2SelectedItemDirective<Select>;

  private defaultItems$ = new BehaviorSubject<Select2Item[]>([]);
  private ngOnDestroy$ = new Subject<void>();

  constructor(public cdRef: ChangeDetectorRef) {
    const items$ = inject(SELECT_ITEMS, { optional: true }) ?? this.getDefaultItems$();
    const filteredItems$ =
      inject(SELECT_FILTERED_ITEMS, { optional: true }) ?? this.getDefaultFilteredItems$();

    items$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(items => this.itemSelector.setItems(items));

    filteredItems$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(items => this.itemsToRender$.next(items));
  }

  public setVirtualScroll(component: VirtualScrollerComponent | undefined) {
    this.virtualScroll$.next(component ?? null);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggle(value?: boolean) {
    const nextValue = value ?? !this.isOpen$.value;
    this.isOpen$.next(nextValue);
  }

  public filterRemove(name: string) {
    const { [name]: _, ...filtersMap } = this.filtersMap;
    this.filtersMap = filtersMap;
  }

  public filterRegister(name: string, fn: SelectFilterFn) {
    this.filtersMap = {
      ...this.filtersMap,
      [name]: fn,
    };
  }

  public filterItems(items: Select2Item[]): Select2Item[] {
    const filters = Object.values(this.filtersMap);
    return items.filter(item => filters.every(filter => filter(item)));
  }

  public itemsRefresh() {
    this.itemsRefresh$.next();
  }

  public scrollToIndex(index: number) {
    if (!this.virtualScroll$.value) return;

    this.virtualScroll$.value.scrollToIndex(index, false, 100);
  }

  public headerTemplateRegister(template: HeaderTemplate) {
    this.headerTemplates = [...this.headerTemplates, template].sort((a, b) =>
      a.index > b.index ? 1 : -1,
    );
  }

  public headerTemplateRemove(name: string) {
    this.headerTemplates = this.headerTemplates.filter(template => template.name !== name);
  }

  public setItems(items: Select2Item[]) {
    this.defaultItems$.next(items);
  }

  public isHovered(itemIndex: number) {
    const navigationIndex = this.navigation$.value.index;
    return navigationIndex === itemIndex;
  }

  private getDefaultFilteredItems$() {
    return this.itemsRefresh$.pipe(
      startWith(null),
      switchMap(() =>
        this.defaultItems$.pipe(
          debounceTime(0),
          map(items => this.filterItems(items)),
        ),
      ),
    );
  }

  private getDefaultItems$() {
    return this.defaultItems$;
  }
}
